import { FC, useMemo, useEffect, useState } from "react"
import { useCustomerBrandCards } from "@ncs/ncs-api"
import {
	AnimatedEntrance,
	Divider,
	GridContainer,
	GridItem,
	GridItemProps,
	Heading,
	Icon,
	LoadingSpinner,
	useScreenSizeMatch,
} from "@ncs/web-legos"
import { ShopBrandCtaEcomm } from "./ShopBrandCtaEcomm"
import { useAuth } from "@ncs/ncs-api"
import { css, Theme } from "@emotion/react"

export const BrandCtaGridEcomm: FC = () => {
	const { user } = useAuth()
	const [cards, cardsLoading, { query }] = useCustomerBrandCards()
	const screenIsMediumUp = useScreenSizeMatch("md", "up")

	const [showAllCards, setShowAllCards] = useState(false)
	const cardsToShow =
		screenIsMediumUp || showAllCards ? (cards ?? []) : (cards?.slice(0, 4) ?? [])

	useEffect(() => {
		async function refetchCards(): Promise<void> {
			await query.refetch()
		}
		refetchCards().catch(console.error)
	}, [JSON.stringify(user), query.refetch])

	// Preload brand card images
	useEffect(() => {
		if (cards && cards.length > 0) {
			cards.forEach((card) => {
				const link = document.createElement("link")
				link.rel = "preload"
				link.as = "image"
				link.href = card.logoImageUrl
				document.head.appendChild(link)
			})
		}
	}, [cards])

	const gridItemProps: GridItemProps = useMemo(() => {
		if (!cards || !cards.length) {
			return {}
		}
		return {
			xs: 12,
			sm: 6,
			md: 4,
		}
	}, [cards])

	if (cardsLoading) {
		return (
			<GridContainer justifyContent="center" alignItems="center" minHeight="200px">
				<LoadingSpinner />
			</GridContainer>
		)
	}

	if (!cards || cards.length === 0) {
		return null
	}

	return (
		<>
			<Heading fontSize={1.5} mb={1} mt={1.5} bold>
				Our Catalogs
			</Heading>
			<GridContainer
				rowGap={screenIsMediumUp ? 3 : 0.75}
				columnGap={3}
				justifyContent="center"
				smProps={{ columnGap: 2 }}
				xsProps={{ columnGap: 0 }}
				minHeight={user ? "inherit" : "450px"}
			>
				{cardsToShow.map((card, i) => (
					<GridItem key={card.id} {...gridItemProps}>
						<AnimatedEntrance show delay={75 * i}>
							<ShopBrandCtaEcomm card={card} />
						</AnimatedEntrance>
					</GridItem>
				))}
			</GridContainer>
			{!screenIsMediumUp && cards.length > 4 && !showAllCards && (
				<button onClick={() => setShowAllCards(!showAllCards)} css={viewMoreButtonCss}>
					{showAllCards ? "Show Less" : "View More"}
					<Icon icon="arrow-down" fontSize={1.5} />
				</button>
			)}
			<Divider my={3} />
		</>
	)
}

const viewMoreButtonCss = (theme: Theme) => css`
	background: none;
	border: none;
	color: ${theme.palette.colors.brandNavy};
	font-weight: 700;
	cursor: pointer;
	font-size: 1rem;
	margin-top: 1rem;
	padding: 0.5rem 1rem;
	text-align: center;
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
`
